import classNames from 'classnames';
import { FormOrientationContext } from 'components/common-form/contexts/FormOrientationContext';
import { useBlockMetadata } from 'components/common-form/hooks/useBlockMetadata';
import { useFieldMetadata } from 'components/common-form/hooks/useFieldMetadata';
import { RefreshSchedule } from 'components/shared/RefreshSchedule/RefreshSchedule';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import type { ProductNode } from 'state/product-description/model';
import { isUserPreferencesDarkMode } from 'state/selectors';
import type { BatchWorkspaceVersion, QuoteWorkspaceVersion } from 'state/userPreferences/userPreferences.model';
import type { WorkspaceType } from 'state/workspaces/model';
import styled from 'styled-components';

import { RenderFormNodeComponent } from '../../FormBuilder';
import type { ProductNodeBuilderProps } from '../ProductNodeBuilder.model';

interface SubChildProps {
  tabId: string;
  subChild: ProductNode;
  level: number;
  workspaceType: WorkspaceType;
  quoteWorkspaceVersion: QuoteWorkspaceVersion;
  batchWorkspaceVersion: BatchWorkspaceVersion;
  mustForceAdvancedMode: boolean;
}

const StyledSubChild = styled.div`
  & {
    margin-left: 1rem;
  }
  &:first-child {
    margin-left: 0;
  }
`;

function SubChild({ subChild, level, workspaceType, ...tabProps }: SubChildProps) {
  const metadata = useFieldMetadata(subChild.id);

  if (metadata?.isHidden === true) {
    return null;
  }
  return (
    <StyledSubChild className={`field-${subChild.id}`}>
      <RenderFormNodeComponent level={level + 1} node={subChild} workspaceType={workspaceType} {...tabProps} />
    </StyledSubChild>
  );
}

const StyledScheduleTopBarBlock = styled.div<{ $isDarkMode: boolean }>`
  background-color: ${({ $isDarkMode }) => ($isDarkMode ? '#303333' : '#fff')};
`;

export function ScheduleTopBarBlock({
  tabId,
  level,
  node: { children, id },
  workspaceType,
  quoteWorkspaceVersion,
  batchWorkspaceVersion,
  mustForceAdvancedMode,
}: ProductNodeBuilderProps) {
  const isDarkMode = useSelector(isUserPreferencesDarkMode);
  const metadata = useBlockMetadata(id);
  const isBatchWorkspace = useMemo(() => workspaceType === 'newBatch', [workspaceType]);
  return (
    <StyledScheduleTopBarBlock
      $isDarkMode={isDarkMode}
      className={classNames(isBatchWorkspace ? 'pb-3 sticky-top' : 'mb-3')}
    >
      <div
        className={classNames(
          'w-100 d-flex h-100 align-items-center',
          isBatchWorkspace ? 'overflow-x-visible' : 'overflow-x-auto',
        )}
      >
        <FormOrientationContext.Provider value="bar">
          {children!
            .filter(child => child.type !== 'ExportGridButton')
            .map(subChild => {
              return (
                <SubChild
                  key={subChild.id}
                  tabId={tabId}
                  subChild={subChild}
                  level={level}
                  workspaceType={workspaceType}
                  quoteWorkspaceVersion={quoteWorkspaceVersion}
                  batchWorkspaceVersion={batchWorkspaceVersion}
                  mustForceAdvancedMode={mustForceAdvancedMode}
                />
              );
            })}
          <div className="flex-grow-1 d-flex text-nowrap justify-content-end h-100">
            {children!
              .filter(child => child.type === 'ExportGridButton')
              .map(subChild => {
                return (
                  <SubChild
                    key={subChild.id}
                    tabId={tabId}
                    subChild={subChild}
                    level={level}
                    workspaceType={workspaceType}
                    quoteWorkspaceVersion={quoteWorkspaceVersion}
                    batchWorkspaceVersion={batchWorkspaceVersion}
                    mustForceAdvancedMode={mustForceAdvancedMode}
                  />
                );
              })}
            {metadata?.type === 'ScheduleTopBar' && metadata.refreshSchedule && (
              <RefreshSchedule tabId={tabId} className="text-secondary btn-flat-secondary" />
            )}
          </div>
        </FormOrientationContext.Provider>
      </div>
    </StyledScheduleTopBarBlock>
  );
}
ScheduleTopBarBlock.displayName = 'ScheduleTopBarBlock';
